/**
 * useBasemapSync — переключает базовую подложку MapLibre без recreate map.
 *
 * Архитектура:
 *   - Map создаётся один раз (см. MapCanvas.tsx) с двумя raster sources:
 *       'base' (Esri World Imagery) + 'roads-overlay' (Esri Transportation).
 *   - Этот hook подписан на useMapStore.basemap и при смене:
 *       1) подменяет tiles у source 'base' через setTiles()
 *       2) переключает visibility у layer 'roads-overlay'
 *
 * Без recreate'а map instance / style — критично для сохранения всех data
 * layers (NDVI raster, fields MVT, crop fills, scope filters).
 *
 * Mapping:
 *   - hybrid    → base = Esri imagery, roads-overlay VISIBLE (default)
 *   - satellite → base = Esri imagery, roads-overlay HIDDEN
 *   - streets   → base = OSM streets,  roads-overlay HIDDEN (дороги уже в тайлах OSM)
 *
 * Spec: AK-36 (BasemapSelector pilot — design-led процесс).
 */
import { useEffect } from 'react';
import type { Map as MLMap, RasterTileSource } from 'maplibre-gl';
import type { Basemap } from './useMapStore';
import {
  basemapTileUrl,
  IMAGERY_TILE_PATH,
  TRANSPORT_TILE_PATH,
} from './basemapUrls';

// Перф-аудит 14.08 (корень 1): подложка идёт через наш кэширующий nginx-прокси,
// а не напрямую в server.arcgisonline.com. Единая точка правды — basemapUrls.ts.
export const ESRI_IMAGERY_URL = basemapTileUrl(IMAGERY_TILE_PATH);

export const ESRI_TRANSPORT_URL = basemapTileUrl(TRANSPORT_TILE_PATH);

/**
 * OSM tile servers — subdomain pool {a,b,c} распределяет нагрузку и обходит
 * лимит браузера на параллельные коннекты к одному origin. MapLibre `setTiles`
 * принимает массив URL'ов и round-robin'ит между ними (а синтаксис {a-c} НЕ
 * поддерживается, в отличие от устаревшего mapbox.js).
 *
 * Usage policy: для прод-нагрузки нужно мигрировать на MapTiler/Stadia,
 * пока pilot — допустимо для < 100 пользователей (см. OSM Tile Usage Policy).
 */
export const OSM_STREETS_URLS = [
  'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
  'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
];

/** Конфиг подложки → URL'ы базового слоя + видимость дорожного оверлея. */
const BASEMAP_CONFIG: Record<Basemap, { baseUrls: string[]; roadsVisible: boolean }> = {
  hybrid: { baseUrls: [ESRI_IMAGERY_URL], roadsVisible: true },
  satellite: { baseUrls: [ESRI_IMAGERY_URL], roadsVisible: false },
  streets: { baseUrls: OSM_STREETS_URLS, roadsVisible: false },
};

/**
 * Идемпотентно применяет конфиг basemap к карте.
 * Безопасно вызывать до style.load — отложит применение через map.once('load').
 */
export function useBasemapSync(map: MLMap | null, basemap: Basemap): void {
  useEffect(() => {
    if (!map) return;

    const apply = () => {
      const cfg = BASEMAP_CONFIG[basemap];
      if (!cfg) return;

      // 1. Подмена tiles у source 'base' (без recreate).
      try {
        const src = map.getSource('base') as RasterTileSource | undefined;
        if (src && typeof src.setTiles === 'function') {
          src.setTiles(cfg.baseUrls);
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[useBasemapSync] setTiles failed', e);
      }

      // 2. Переключение visibility у roads-overlay layer.
      try {
        if (map.getLayer('roads-overlay')) {
          map.setLayoutProperty(
            'roads-overlay',
            'visibility',
            cfg.roadsVisible ? 'visible' : 'none',
          );
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[useBasemapSync] setLayoutProperty roads-overlay failed', e);
      }
    };

    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once('load', apply);
      return () => {
        map.off('load', apply);
      };
    }
  }, [map, basemap]);
}
